import { __assign } from "tslib";
import React, { useRef, useEffect, useContext } from 'react';
import classnames from 'classnames';
import omitBy from 'lodash/omitBy';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import CheckIcon from '@wix/da-ds/pkg/Icons/Check';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import StandardMenuItemLabel from '../StandardMenuItemLabel';
import StandardMenuSubmenu from '../StandardMenuSubmenu';
import StandardMenuContext from '../context';
import s from './StandardMenuOption.scss';
/**
 * A parent of StandardMenuOption could be a multitude of roles. Each parent role may require a certain role be applied to this component.
 *
 * This returns the correct role for the supplied `parentRole`.
 */
function getRoleBasedOnParent(
// The `string | undefined` part here is to get around some typing issues
parentRole) {
    switch (parentRole) {
        case 'listbox':
            return 'option';
        case 'menu':
            return 'menuitem';
        default:
            break;
    }
}
var StandardMenuOption = function (_a) {
    var id = _a.id, item = _a.item, _b = _a.item, itemIcon = _b.icon, iconClassName = _b.iconClassName, onClick = _b.onClick, onMouseEnter = _b.onMouseEnter, disabled = _b.disabled, danger = _b.danger, itemClassName = _b.className, value = _b.value, highlighted = _b.highlighted, selected = _b.selected, label = _b.label, biData = _b.biData, dataHook = _b.dataHook, tabIndex = _b.tabIndex, link = _b.link, target = _b.target, title = _b.title, submenu = _b.submenu, subMenuPreferredSide = _b.subMenuPreferredSide, estimatedSubmenuWidth = _b.estimatedSubmenuWidth, minimumHeightNeededForSubmenu = _b.minimumHeightNeededForSubmenu, hideCheckIcon = _a.hideCheckIcon, withHoverEffect = _a.withHoverEffect, className = _a.className, focused = _a.focused;
    var parentRole = useContext(StandardMenuContext).role;
    var icon = null;
    if (itemIcon) {
        // ick
        icon = React.cloneElement(itemIcon, {
            className: classnames(s['menu-item-icon'], iconClassName),
        });
    }
    var itemRef = useRef(null);
    useEffect(function () {
        if (itemRef.current && focused) {
            itemRef.current.focus({ preventScroll: true });
        }
    }, [itemRef, focused]);
    var content = (React.createElement(React.Fragment, null,
        icon,
        React.createElement(StandardMenuItemLabel, { item: item }),
        selected && !hideCheckIcon && (React.createElement(CheckIcon, { className: s['selected-icon'], size: IconSize.TINY }))));
    // omitBy to prevent undefined values from overwriting props that we need.
    var sharedProps = omitBy({
        id: id,
        innerRef: function (node) {
            itemRef.current = node;
        },
        className: classnames(s['menu-item'], onClick && s['clickable'], disabled && s['disabled'], danger && s['danger'], withHoverEffect && s['with-hover-effect'], highlighted && s['highlighted'], itemClassName, className),
        value: value,
        disabled: disabled,
        selected: selected,
        label: typeof label === 'string' ? label : undefined,
        onClick: onClick,
        onMouseEnter: onMouseEnter,
        biData: biData,
        // We don't want to tab into items individually, the wrapping StandardMenuContent will manage
        // focus itself.
        tabIndex: tabIndex === undefined ? -1 : tabIndex,
        children: content,
        title: title,
        role: getRoleBasedOnParent(parentRole),
        'data-hook': dataHook,
        // Undefined on false to avoid some extra text being read out in VoiceOver + Chrome
        'aria-selected': selected ? true : undefined,
        'aria-disabled': disabled,
        'data-selected': selected ? 'selected' : undefined,
    }, function (keyValue) { return keyValue === undefined; });
    if (link) {
        return React.createElement(BiLink, __assign({ href: link, target: target }, sharedProps));
    }
    else if (submenu) {
        // A11Y TODO: Figure out what role these should have
        // Should likely be the usual `role="getRoleBasedOnParent(...)"` with an `aria-haspopup`
        // https://www.digitala11y.com/option-role/
        return (React.createElement(StandardMenuSubmenu, __assign({ submenu: submenu, preferredSide: subMenuPreferredSide, estimatedSubmenuWidth: estimatedSubmenuWidth, minimumHeightNeeded: minimumHeightNeededForSubmenu }, sharedProps)));
    }
    else {
        return React.createElement(PlainButtonOrLink, __assign({ isFakeButton: true }, sharedProps));
    }
};
StandardMenuOption.displayName = 'StandardMenuOption';
export default StandardMenuOption;
//# sourceMappingURL=StandardMenuOption.js.map